import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, exigir, ErroApi } from "../api/cliente";
import { BarraFiltros, GrupoMenu, MenuFiltro, OpcaoMenu } from "../componentes/kit/BarraFiltros";
import { Botao } from "../componentes/kit/Botao";
import { CabecalhoTela } from "../componentes/kit/CabecalhoTela";
import { Esqueleto, Falha, Vazio } from "../componentes/kit/Estados";
import { LinhaProcesso, ListaProcessos } from "../componentes/kit/ListaProcessos";
import { usePainel } from "../componentes/kit/painel";
import { NovoProcesso } from "./NovoProcesso";
import { Plus } from "lucide-react";

type Status = "ativo" | "suspenso" | "arquivado" | "encerrado";
const SITUACOES: { valor: Status | null; rotulo: string }[] = [
  { valor: null, rotulo: "Todas" },
  { valor: "ativo", rotulo: "Ativos" },
  { valor: "suspenso", rotulo: "Suspensos" },
  { valor: "arquivado", rotulo: "Arquivados" },
  { valor: "encerrado", rotulo: "Encerrados" },
];

/** E3-03: lista e busca de processos (nome, CNJ, CPF ou telefone), paginada por cursor. */
export function Processos() {
  const { busca: buscaUrl } = useSearch({ from: "/logado/processos" });
  const navegar = useNavigate({ from: "/processos" });
  const [digitado, setDigitado] = useState(buscaUrl ?? "");
  const [busca, setBusca] = useState(buscaUrl ?? "");
  const [status, setStatus] = useState<Status | null>(null);
  const [meus, setMeus] = useState(false);
  const [cadastrando, setCadastrando] = useState(false);
  usePainel(useMemo(() => (cadastrando ? <NovoProcesso aoFechar={() => setCadastrando(false)} /> : null), [cadastrando]));

  // Quem chega pelo Ctrl+K traz a busca na URL.
  useEffect(() => { setDigitado(buscaUrl ?? ""); setBusca(buscaUrl ?? ""); }, [buscaUrl]);

  // Busca 250 ms depois de parar de digitar; a URL acompanha (dá para voltar e compartilhar).
  useEffect(() => {
    const id = window.setTimeout(() => {
      const termo = digitado.trim();
      setBusca(termo);
      navegar({ search: termo ? { busca: termo } : {}, replace: true });
    }, 250);
    return () => window.clearTimeout(id);
  }, [digitado, navegar]);

  const consulta = useInfiniteQuery({
    queryKey: ["processos", { busca, status, meus }],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => exigir(api.GET("/api/processos", {
      params: { query: { busca: busca || undefined, status: status ?? undefined, meus, limite: 100, depois: pageParam ?? undefined } },
    })),
    getNextPageParam: (ultima) => ultima.proximo,
  });

  // Carrega a próxima página quando o fim da lista aparece na tela.
  const fim = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = fim.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e?.isIntersecting && consulta.hasNextPage && !consulta.isFetchingNextPage) consulta.fetchNextPage();
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [consulta]);

  const itens = consulta.data?.pages.flatMap((p) => p.itens) ?? [];
  const filtro = [status && SITUACOES.find((s) => s.valor === status)!.rotulo, meus && "Só os meus"].filter(Boolean).join(" · ");

  return (
    <div>
      <CabecalhoTela titulo="Processos" contexto="atividade mais recente primeiro"
        acoes={<Botao variante="primario" icone={<Plus size={14} aria-hidden />} onClick={() => setCadastrando(true)}>Novo processo</Botao>} />
      <BarraFiltros busca={digitado} aoBuscar={setDigitado} placeholder="Nome, número, CPF ou telefone"
        total={consulta.data ? `${itens.length}${consulta.hasNextPage ? "+" : ""} ${itens.length === 1 ? "processo" : "processos"}` : undefined}>
        <MenuFiltro rotulo="Filtro" valor={filtro || null}>
          <GrupoMenu titulo="Situação">
            {SITUACOES.map((s) => <OpcaoMenu key={s.rotulo} marcada={status === s.valor} aoEscolher={() => setStatus(s.valor)}>{s.rotulo}</OpcaoMenu>)}
          </GrupoMenu>
          <div className="fio-t" />
          <GrupoMenu titulo="Responsável">
            <OpcaoMenu marcada={!meus} aoEscolher={() => setMeus(false)}>Todos</OpcaoMenu>
            <OpcaoMenu marcada={meus} aoEscolher={() => setMeus(true)}>Só os meus</OpcaoMenu>
          </GrupoMenu>
        </MenuFiltro>
      </BarraFiltros>

      {consulta.isPending ? (
        <Esqueleto linhas={8} />
      ) : consulta.isError ? (
        <Falha mensagem={consulta.error instanceof ErroApi ? consulta.error.message : "Não foi possível carregar os processos."}
          aoTentar={() => consulta.refetch()} />
      ) : itens.length === 0 ? (
        <Vazio titulo={busca ? `Nada encontrado para “${busca}”` : "Nenhum processo com esses filtros"}>
          {busca ? "Confira o número ou tente parte do nome do cliente." : "Troque a situação ou o responsável no Filtro."}
        </Vazio>
      ) : (
        <>
          <ListaProcessos>{itens.map((p) => <LinhaProcesso key={p.numero} p={p} />)}</ListaProcessos>
          <div ref={fim} className="flex justify-center py-4">
            {consulta.hasNextPage && (
              <Botao variante="fantasma" carregando={consulta.isFetchingNextPage} onClick={() => consulta.fetchNextPage()}>
                {consulta.isFetchingNextPage ? "Carregando…" : "Carregar mais"}
              </Botao>
            )}
          </div>
        </>
      )}
    </div>
  );
}
